import { Button, IconSet, Panel, ProgressBar } from '@postscript/components';
import { useUser } from 'controllers/contexts/user';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import styled from 'styled-components';
import { COMMON_EVENT_NAMES, logEvent } from 'utils/events';

import { WorkflowAside } from 'components/ProgressiveOnboarding/WorkflowAside';
import { WorkflowStep } from 'components/ProgressiveOnboarding/WorkflowStep';

import {
  PricingTooltipPortal,
  PRICING_TOOLTIP_ID,
} from 'components/billing/admin/promotions/BundledAI/shared/PricingTooltip';
import {
  BUNDLED_AI_FEATURE_KEY,
  PROGRESSIVE_ONBOARDING_SOURCE,
} from 'providers/ProgressiveOnboarding/constants/constants';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';
import type { Guide } from 'providers/ProgressiveOnboarding/types';

const StyledPanelContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: var(--spacing-2);
`;

const StyledStepList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-2);
  align-self: stretch;
`;

const StyledFooterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

interface OnboardingPanelContentProps {
  guide: Guide;
}

const OnboardingPanelContent = ({ guide }: OnboardingPanelContentProps) => {
  const { steps, callout } = guide;

  const [openStep, setOpenStep] = useState<string | null>(
    steps.find((step) => step.state === 'incomplete')?.key ?? null,
  );

  const toggleStep = (stepKey: string) => {
    setOpenStep((prev) => (prev === stepKey ? null : stepKey));
  };

  return (
    <StyledPanelContent>
      {callout && <WorkflowAside {...callout} />}
      <StyledStepList>
        {steps.map((step) => (
          <WorkflowStep
            {...step}
            key={step.key}
            isOpen={step.key === openStep}
            toggleStep={() => toggleStep(step.key)}
            primaryAction={
              step.PrimaryAction ? (
                <step.PrimaryAction step={step} />
              ) : undefined
            }
            secondaryAction={
              step.SecondaryAction ? (
                <step.SecondaryAction step={step} />
              ) : undefined
            }
            nodeAbove={step.nodeAbove}
            tooltip={step.tooltip}
          />
        ))}
      </StyledStepList>
    </StyledPanelContent>
  );
};

interface OnboardingPanelInnerProps {
  guide: Guide;
  onBack?: () => void;
}

const OnboardingPanelInner = ({ guide, onBack }: OnboardingPanelInnerProps) => {
  const { setActiveGuide, allGuides, dismissGuide } =
    useProgressiveOnboarding();
  const { user } = useUser();

  const [splashDismissed, setSplashDismissed] = useLocalStorage(
    `progressive_onboarding_${guide.key}_splash_dismissed`,
    false,
  );

  // Use the latest guide state from allGuides to stay in sync after markStepCompleted
  const freshGuide = allGuides.find((g) => g.key === guide.key) ?? guide;

  const { title, icon, steps, key, productArea } = freshGuide;
  const totalSteps = steps.length;
  const completedSteps = steps.filter(
    (step) => step.state === 'complete',
  ).length;

  const isBundledAI = key === BUNDLED_AI_FEATURE_KEY;
  const showSplash = freshGuide.splashScreen && !splashDismissed;

  useEffect(() => {
    logEvent(COMMON_EVENT_NAMES.BUTTON_CLICKED, {
      text: `Open ${title} Guide`,
      productArea,
      source: PROGRESSIVE_ONBOARDING_SOURCE,
      workflow: key,
      shop_id: user.shop_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    logEvent('onboarding guide closed', {
      productArea,
      source: PROGRESSIVE_ONBOARDING_SOURCE,
      workflow: key,
      shop_id: user.shop_id,
    });
    setActiveGuide(undefined);
  };

  const handleCancelButton = () => {
    // If the user clicks X on a splash that was reached via a sidebar promo
    // card, also dismiss that promo card from the sidebar. Backdrop clicks
    // (which call handleClose directly) intentionally don't trigger this.
    const hasPromoCard = freshGuide.steps.some((s) => s.promoCard);
    if (showSplash && hasPromoCard) {
      dismissGuide({ guideKey: key, setupName: title });
    }
    handleClose();
  };

  const handleBack = () => {
    logEvent('onboarding guide back to launchpad', {
      productArea,
      source: PROGRESSIVE_ONBOARDING_SOURCE,
      workflow: key,
      shop_id: user.shop_id,
    });
    setActiveGuide(undefined);
    onBack?.();
  };

  const SplashScreen = freshGuide.splashScreen;

  return (
    <Panel
      isOpen
      onCancel={handleClose}
      placement="drawer"
      dismissOnBackdropClick
      visibleBackdrop={false}
      width={444}
    >
      <Panel.Header
        onCancel={handleCancelButton}
        onBack={handleBack}
        showBackButton
        icon={IconSet[icon]}
        iconBackgroundColor="var(--blue-1)"
        additionalContent={
          <ProgressBar
            step={Math.min(completedSteps + 1, totalSteps)}
            totalSteps={totalSteps}
          />
        }
        showAdditionalContent={!showSplash}
      >
        {title}
      </Panel.Header>

      <Panel.Body>
        {showSplash && SplashScreen ? (
          <SplashScreen dismiss={() => setSplashDismissed(true)} />
        ) : (
          <OnboardingPanelContent guide={freshGuide} />
        )}
      </Panel.Body>

      {isBundledAI && !showSplash && (
        <Panel.Footer>
          <StyledFooterContent>
            <Button
              variant="text"
              icon={IconSet.Information}
              data-for={PRICING_TOOLTIP_ID}
              data-tip
              aria-describedby={PRICING_TOOLTIP_ID}
            >
              View Pricing Details
            </Button>
            <PricingTooltipPortal />
          </StyledFooterContent>
        </Panel.Footer>
      )}
    </Panel>
  );
};

interface OnboardingPanelProps {
  onBack?: () => void;
}

export const OnboardingPanel = ({ onBack }: OnboardingPanelProps) => {
  const { activeGuide } = useProgressiveOnboarding();

  if (!activeGuide) return null;

  return <OnboardingPanelInner guide={activeGuide} onBack={onBack} />;
};
